import {
  collectNormalizedPlans,
  NormalizedIntegration,
  NormalizedPlan,
} from "./IntegrationDataDisplay.adapters";
import {
  AgreementMappingFlags,
  MappedPlanSlug,
} from "./IntegrationDataDisplay.types";

/**
 * Replica a regra de match do backend.
 *
 * partiu-api → MapIntegrationPlan.get_mapped_plan_or_default:
 *
 *   plan_slug_integration_clean = Replace(plan_slug_integration, "\xa0", " ")
 *   plan_slug_integration_lower = Lower(plan_slug_integration_clean)
 *   .filter(plan_slug_integration_lower__in=_normalize_slug_list(slugs))
 *
 * e _normalize_slug_list = str(slug).strip().lower()
 *
 * Ou seja: a comparação é CASE-INSENSITIVE nos dois lados, com NBSP virando
 * espaço comum. Diferença de maiúsculas/minúsculas NÃO impede o mapeamento.
 *
 * O IXC ainda remove o sufixo ".0001".."0005" antes de comparar
 * (IXCClient.normalize_integration_slug).
 */

export type MatchKind = "exact" | "none";

export interface IntegrationPlanEntry extends NormalizedPlan {
  /** De onde esse texto veio (o mesmo plano costuma vir de vários lugares) */
  origins: string[];
  match?: MappedPlanSlug;
  matchKind: MatchKind;
  /** Foi o id ou o nome que bateu com o cadastro? */
  matchedBy?: "id" | "name";
}

export type MatchStatus = "no-mappings" | "no-plans" | "none" | "partial" | "ok";

export interface MatchSummary {
  status: MatchStatus;
  entries: IntegrationPlanEntry[];
  matched: IntegrationPlanEntry[];
  unmatched: IntegrationPlanEntry[];
  usedMappings: MappedPlanSlug[];
  unusedMappings: MappedPlanSlug[];
  totalMappings: number;
  erpLoaded: boolean;
  totalErpSlugs: number;
  unmappedErpSlugs: string[];
  /** Qual chave o backend está comparando neste convênio */
  matchKeyLabel: string;
}

/** Mesma normalização do backend: NBSP → espaço, trim, lower */
export function normalizeSlug(value: string | undefined | null): string {
  return String(value ?? "")
    .replace(/ /g, " ")
    .trim()
    .toLowerCase();
}

/** IXCClient.normalize_integration_slug — remove sufixo .0001 .. .0005 */
export function stripIxcSuffix(value: string | undefined | null): string {
  return String(value ?? "")
    .trim()
    .replace(/\.000[1-5]$/, "");
}

/** Formas equivalentes de um mesmo texto para efeito de comparação */
function slugVariants(value: string | undefined | null): string[] {
  const base = normalizeSlug(value);
  const stripped = normalizeSlug(stripIxcSuffix(value));
  return base === stripped ? [base] : [base, stripped];
}

export function findMappedSlug(
  candidate: string | undefined | null,
  mappedPlanSlugs: MappedPlanSlug[]
): MappedPlanSlug | undefined {
  const variants = slugVariants(candidate).filter(Boolean);
  if (!variants.length) return undefined;

  return mappedPlanSlugs.find((mapping) => {
    const registered = slugVariants(mapping.plan_slug_integration);
    return registered.some((value) => value && variants.includes(value));
  });
}

function findMappedById(
  id: string | undefined,
  mappedPlanSlugs: MappedPlanSlug[]
): MappedPlanSlug | undefined {
  if (!id) return undefined;
  const normalized = normalizeSlug(id);
  return mappedPlanSlugs.find(
    (mapping) =>
      mapping.id_plan != null &&
      normalizeSlug(String(mapping.id_plan)) === normalized
  );
}

/**
 * Junta planos repetidos (o mesmo texto costuma vir de vários lugares) em uma
 * linha só, preservando as origens.
 */
function dedupe(plans: NormalizedPlan[]): IntegrationPlanEntry[] {
  const byKey = new Map<string, IntegrationPlanEntry>();

  plans.forEach((plan) => {
    if (!plan.name) return;
    const key = normalizeSlug(plan.name);
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, {
        ...plan,
        origins: [plan.source],
        matchKind: "none",
      });
      return;
    }

    if (!existing.origins.includes(plan.source)) {
      existing.origins.push(plan.source);
    }
    existing.id = existing.id ?? plan.id;
    existing.code = existing.code ?? plan.code;
    existing.amount = existing.amount ?? plan.amount;
  });

  return Array.from(byKey.values());
}

function describeMatchKey(flags: AgreementMappingFlags): string {
  if (flags.mappedById && flags.mappedByProduct) return "id do produto";
  if (flags.mappedById) return "id do plano";
  if (flags.mappedByProduct) return "descrição do produto";
  return "nome do plano";
}

export function buildMatchSummary(
  integration: NormalizedIntegration,
  mappedPlanSlugs: MappedPlanSlug[] = [],
  /** Catálogo do ERP (v1/get_plans_erp → data[].slug). undefined = não carregado */
  erpSlugs?: string[],
  flags: AgreementMappingFlags = {}
): MatchSummary {
  const entries = dedupe(collectNormalizedPlans(integration)).map((entry) => {
    const byId = findMappedById(entry.id, mappedPlanSlugs);
    const byName = findMappedSlug(entry.name, mappedPlanSlugs);

    // O backend prioriza id quando o convênio mapeia por id.
    const match = flags.mappedById ? byId ?? byName : byName ?? byId;
    const matchedBy: IntegrationPlanEntry["matchedBy"] = match
      ? match === byId && byId !== byName
        ? "id"
        : "name"
      : undefined;

    return {
      ...entry,
      match,
      matchKind: (match ? "exact" : "none") as MatchKind,
      matchedBy,
    };
  });

  // Sem mapeamento primeiro — é o que precisa de ação.
  entries.sort((a, b) => {
    if (a.matchKind !== b.matchKind) return a.matchKind === "none" ? -1 : 1;
    return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
  });

  const matched = entries.filter((entry) => entry.matchKind === "exact");
  const unmatched = entries.filter((entry) => entry.matchKind === "none");

  const usedSlugs = new Set(
    matched.map((entry) => normalizeSlug(entry.match?.plan_slug_integration))
  );
  const usedMappings = mappedPlanSlugs.filter((mapping) =>
    usedSlugs.has(normalizeSlug(mapping.plan_slug_integration))
  );
  const unusedMappings = mappedPlanSlugs.filter(
    (mapping) => !usedSlugs.has(normalizeSlug(mapping.plan_slug_integration))
  );

  const registered = new Set(
    mappedPlanSlugs.flatMap((mapping) =>
      slugVariants(mapping.plan_slug_integration)
    )
  );
  const unmappedErpSlugs = (erpSlugs ?? []).filter(
    (slug) => !slugVariants(slug).some((value) => registered.has(value))
  );

  let status: MatchStatus;
  if (!mappedPlanSlugs.length) status = "no-mappings";
  else if (!entries.length) status = "no-plans";
  else if (!matched.length) status = "none";
  else if (unmatched.length) status = "partial";
  else status = "ok";

  return {
    status,
    entries,
    matched,
    unmatched,
    usedMappings,
    unusedMappings,
    totalMappings: mappedPlanSlugs.length,
    erpLoaded: erpSlugs !== undefined,
    totalErpSlugs: erpSlugs?.length ?? 0,
    unmappedErpSlugs,
    matchKeyLabel: describeMatchKey(flags),
  };
}
